import type ApiClient from "../apiClient";
import {
  PsychService as LegacyPsychService,
  type HeartbeatRiskStatus,
  type PsychProfile,
  type PsychScores,
} from "./ai";

export type WearableProvider = "whoop" | "fitbit";
export type SetupStatus = "required" | "complete" | "skipped";
export type ERLevel = "unknown" | "steady" | "elevated" | "high" | "critical";
export type SessionEndReason =
  "user_stopped" | "disconnect_timeout" | "setup_required" | "error";

export interface WearableConnection {
  id: string;
  provider: WearableProvider;
  displayLabel: string;
  scopes: string[];
  status: "connected" | "stale" | "revoked" | "error";
  connectedAt: string;
  lastSyncAt: string | null;
  lastSampleAt: string | null;
}

export interface PsychSetupStatus {
  setup: {
    status: SetupStatus;
    version: string;
    source: string;
    updatedAt: string | null;
  };
  connections: WearableConnection[];
  providers: Array<{
    id: WearableProvider;
    label: string;
    scopes: string[];
    configured: boolean;
  }>;
  requiresSetup: boolean;
}

export interface PsychSession {
  sessionId: number;
  startedAt: string;
  endedAt: string | null;
  isFinalized: boolean;
  opening: {
    score: number | null;
    level: ERLevel;
    recommendation: string;
    canStart: boolean;
    sourceProvider: WearableProvider | null;
    sourceFreshnessSeconds: number | null;
  };
  finalScore: number | null;
  averageEr: number | null;
  durationSeconds: number;
  endReason: SessionEndReason | null;
  rollups: Array<{
    bucketStart: string;
    bucketEnd: string;
    avgEr: number | null;
    minEr: number | null;
    maxEr: number | null;
    sampleCount: number;
  }>;
}

export class PsychService extends LegacyPsychService {
  constructor(private psychClient: ApiClient) {
    super(psychClient);
  }

  async getSetupStatus(): Promise<PsychSetupStatus> {
    return this.psychClient.get<PsychSetupStatus>("/api/psych/setup/status");
  }

  async completeSetup(data: {
    status?: SetupStatus;
    source?: string;
  }): Promise<PsychSetupStatus["setup"]> {
    const response = await this.psychClient.post<{
      setup: PsychSetupStatus["setup"];
    }>("/api/psych/setup/complete", data);
    return response.setup;
  }

  async startWearableOAuth(provider: WearableProvider): Promise<{
    authUrl: string;
    state: string;
    expiresAt: string;
  }> {
    return this.psychClient.get(`/api/psych/wearables/${provider}/oauth/start`);
  }

  async syncWearable(connectionId: string): Promise<{
    connection: WearableConnection;
    sample: unknown | null;
  }> {
    return this.psychClient.post(
      `/api/psych/wearables/${connectionId}/sync`,
      {},
    );
  }

  async startSession(): Promise<PsychSession> {
    const response = await this.psychClient.post<{ session: PsychSession }>(
      "/api/psych/sessions/start",
      {},
    );
    return response.session;
  }

  async finalizeSession(
    sessionId: number,
    endReason: SessionEndReason,
  ): Promise<PsychSession> {
    const response = await this.psychClient.post<{ session: PsychSession }>(
      `/api/psych/sessions/${sessionId}/finalize`,
      { endReason },
    );
    return response.session;
  }

  async listSessions(limit = 20): Promise<PsychSession[]> {
    const response = await this.psychClient.get<{ sessions: PsychSession[] }>(
      `/api/psych/sessions?limit=${limit}`,
    );
    return response.sessions;
  }

  async getSession(sessionId: number): Promise<PsychSession> {
    const response = await this.psychClient.get<{ session: PsychSession }>(
      `/api/psych/sessions/${sessionId}`,
    );
    return response.session;
  }
}

export type { HeartbeatRiskStatus, PsychProfile, PsychScores };
